import AddOutlinedIcon from '@material-ui/icons/AddOutlined'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { useFormikContext } from 'formik'
import * as Analytics from 'core/common/analytics'
import { NOT_APPLICABLE_FIELD_VALUE } from 'core/common/constants/datasheet'
import i18n from 'core/common/i18n'
import { validateField, getErrorMessage } from 'core/common/utils/validation'
import {
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
} from 'core/components/Core'
import TextField from 'core/components/TextField'
import { useAppContext } from 'core/pages/ProductNew-v2/context/AppContext'
import { generateVariation } from '../../helper'
import * as S from '../../style'
import { AttributeField } from '../AttributeField'
import UploadImageField from '../UploadImageField'
import VariationHeader from './VariationHeader'

const VariationsAccordion = ({
  variationsGrouped,
  expanded,
  handleAccordionChange,
  handleEditStep,
  isEditMode,
  setOpenEanTips,
  customAttributesTypes,
  isAddingOtherAttributes,
  setIsAddingOtherAttributes,
  setVariationType,
  hasVariations,
  step,
  formData,
  formMode,
  saveNewVariations,
  isAddingMainAttribute,
  onAddMainAttribute,
  setIsAddingMainAttribute,
  variationTypes,
  custom,
  setProgressState,
  isURLImageActive,
  updateImages,
  setIsURLImageActive,
}) => {
  const { values, errors, touched, submitCount, setFieldValue, handleChange, handleBlur } =
    useFormikContext()
  const { defaultVariationOptionsByType } = useAppContext()

  return (
    <>
      {Object.entries(variationsGrouped)?.map(([variationGroupName, variationGroupList]) => {
        return (
          <div key={variationGroupName}>
            <Accordion
              style={{ boxShadow: 'none' }}
              expanded={expanded.includes(variationGroupName)}
              onChange={handleAccordionChange(variationGroupName, values)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                style={{ paddingLeft: 0, paddingRight: 0 }}
              >
                <VariationHeader
                  variationGroupName={variationGroupName}
                  variationGroupList={variationGroupList}
                  isEditMode={isEditMode}
                  customAttributesTypes={customAttributesTypes}
                  variationTypes={variationTypes}
                  defaultVariationOptionsByType={defaultVariationOptionsByType}
                />
              </AccordionSummary>
              <AccordionDetails style={{ paddingLeft: 0, paddingRight: 0 }}>
                {expanded.includes(variationGroupName) && (
                  <div className="w-100">
                    <UploadImageField
                      variationGroupList={variationGroupList}
                      formData={formData}
                      hasVariations={hasVariations}
                      formMode={formMode}
                      custom={custom}
                      setProgressState={setProgressState}
                      isURLImageActive={isURLImageActive}
                      handleEditStep={handleEditStep}
                      updateImages={updateImages}
                      step={step}
                      setIsURLImageActive={setIsURLImageActive}
                    />
                    {variationGroupList?.map(variationItem => {
                      const variationIndex = formData.variations.findIndex(
                        item => item.id === variationItem.id
                      )

                      const newVariationItem = variationItem.newVariation

                      return (
                        <div
                          key={variationItem.id}
                          className="py-3 pl-3 pr-2 mt-3"
                          style={{ backgroundColor: '#F7F7F7' }}
                        >
                          <div className="d-flex justify-between align-center mb-1">
                            <div>
                              {variationItem.customAttributes?.map((attribute, index) => {
                                const lastItem = index === variationItem.customAttributes.length - 1
                                return (
                                  <Typography key={attribute.type} fontSize={20} component="span">
                                    {i18n.t(`multivariation.types.${attribute.type}`)}:{' '}
                                    {attribute.value}
                                    {!lastItem && <span className="mx-1">/</span>}
                                  </Typography>
                                )
                              })}
                            </div>
                          </div>
                          <div className="d-flex">
                            <div className="d-flex flex-column mr-4">
                              <div className="d-flex">
                                <S.FixErrorFieldSku>
                                  <TextField
                                    fullWidth
                                    name={`variations[${variationIndex}].sku`}
                                    variant="outlined"
                                    label="SKU"
                                    value={values.variations?.[variationIndex]?.sku}
                                    onChange={e => {
                                      handleChange(e)
                                      handleEditStep(step.key)
                                    }}
                                    onBlur={handleBlur}
                                    error={validateField(
                                      `variations[${variationIndex}].sku`,
                                      errors,
                                      touched,
                                      submitCount > 0
                                    )}
                                    helperText={getErrorMessage(
                                      `variations[${variationIndex}].sku`,
                                      errors,
                                      touched
                                    )}
                                    disabled={isEditMode && !newVariationItem}
                                  />
                                </S.FixErrorFieldSku>
                              </div>
                            </div>

                            <div className="d-flex flex-column flex-1">
                              <div className="d-flex mb-3">
                                <TextField
                                  fullWidth
                                  name={`variations[${variationIndex}].ean`}
                                  variant="outlined"
                                  label="EAN"
                                  value={values.variations?.[variationIndex]?.ean}
                                  onChange={e => {
                                    handleChange(e)
                                    handleEditStep(step.key)
                                  }}
                                  onBlur={handleBlur}
                                  style={{ backgroundColor: '#fff' }}
                                  error={validateField(
                                    `variations[${variationIndex}].ean`,
                                    errors,
                                    touched,
                                    submitCount > 0
                                  )}
                                  helperText={getErrorMessage(
                                    `variations[${variationIndex}].ean`,
                                    errors,
                                    touched
                                  )}
                                  InputLabelProps={{
                                    shrink: Boolean(values?.variations?.[variationIndex]?.ean),
                                  }}
                                  disabled={values.variations?.[variationIndex]?.not_has_ean}
                                />

                                <HelpOutlineIcon
                                  style={{ marginLeft: 4, fontSize: 16 }}
                                  color="primary"
                                  onClick={() => setOpenEanTips(true)}
                                  className="cursor-pointer"
                                />
                              </div>
                              <div>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      defaultChecked={
                                        values.variations?.[variationIndex]?.not_has_ean
                                      }
                                    />
                                  }
                                  label={i18n.t('productNew.regulatoryInformation.notApply')}
                                  onChange={(event, checked) => {
                                    if (checked) {
                                      setFieldValue(
                                        `variations[${variationIndex}].not_has_ean`,
                                        true
                                      )
                                      setFieldValue(
                                        `variations[${variationIndex}].ean`,
                                        NOT_APPLICABLE_FIELD_VALUE
                                      )

                                      handleEditStep(step.key)
                                      return
                                    }

                                    setFieldValue(`variations[${variationIndex}].ean`, '')
                                    setFieldValue(
                                      `variations[${variationIndex}].not_has_ean`,
                                      false
                                    )

                                    handleEditStep(step.key)
                                  }}
                                  value={values.variations?.[variationIndex]?.not_has_ean}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}

                    {isEditMode && isAddingOtherAttributes && (
                      <div className="py-3 pl-3 pr-2 mt-3" style={{ backgroundColor: '#F7F7F7' }}>
                        <div className="d-flex justify-between">
                          <div className="d-flex flex-1">
                            <div className="d-flex flex-1 flex-column mr-4">
                              <S.AttributeFieldBox className="d-flex mb-3">
                                <AttributeField
                                  name="secondAttribute"
                                  variationType={customAttributesTypes[1]}
                                  style={{ backgroundColor: '#fff' }}
                                  className="mr-2 flex-1"
                                />
                              </S.AttributeFieldBox>
                            </div>

                            <div className="d-flex flex-column flex-1">
                              <S.AttributeFieldBox className="d-flex mb-3">
                                <AttributeField
                                  name="thirdAttribute"
                                  variationType={customAttributesTypes[2]}
                                  style={{ backgroundColor: '#fff' }}
                                  className="flex-1"
                                />
                              </S.AttributeFieldBox>
                            </div>
                          </div>

                          {isEditMode && hasVariations && (
                            <div className="d-flex justify-end" style={{ flex: 0.6 }}>
                              <DeleteOutlineIcon
                                color="error"
                                onClick={() => {
                                  setVariationType('second', '')
                                  setVariationType('third', '')
                                  setFieldValue('secondAttribute', [])
                                  setFieldValue('thirdAttribute', [])
                                  setIsAddingOtherAttributes(false)
                                }}
                                className="cursor-pointer"
                              />
                            </div>
                          )}
                        </div>

                        <div className="mt-1">
                          <Button
                            color="primary"
                            variant="outlined"
                            onClick={() => {
                              const newVariation = generateVariation({
                                values,
                                variationGroupName,
                                variationGroupList,
                                customAttributesTypes,
                              })

                              Analytics.ProductVariationAttributeSaved(formMode)
                              saveNewVariations({ values, newVariations: [newVariation] })
                            }}
                            disabled={
                              customAttributesTypes.length < 3
                                ? !values.secondAttribute
                                : !values.secondAttribute || !values.thirdAttribute
                            }
                          >
                            Adicionar
                          </Button>
                        </div>
                      </div>
                    )}

                    {isEditMode && !isAddingOtherAttributes && !!customAttributesTypes[1] && (
                      <Button
                        color="primary"
                        startIcon={<AddOutlinedIcon color="primary" />}
                        variant="outlined"
                        onClick={() => {
                          setIsAddingOtherAttributes(true)
                          setVariationType('second', customAttributesTypes[1])
                          setVariationType('third', customAttributesTypes[2])
                        }}
                        className="mt-4"
                      >
                        {i18n.t('productNew.add')}{' '}
                        {customAttributesTypes[1]
                          ? i18n.t(`multivariation.types.${customAttributesTypes[1]}`)
                          : ''}
                        {customAttributesTypes[1] && customAttributesTypes[2]
                          ? ` e ${i18n.t(`multivariation.types.${customAttributesTypes[2]}`)}`
                          : ''}
                      </Button>
                    )}
                  </div>
                )}
              </AccordionDetails>
            </Accordion>
            <Divider />
          </div>
        )
      })}

      {isEditMode && isAddingMainAttribute && (
        <AttributeField
          name="firstAttribute"
          handleSubmit={() => {
            Analytics.ProductVariationAttributeSaved(formMode)
            onAddMainAttribute(values)
          }}
          handleDelete={() => {
            setVariationType('first', '')
            setIsAddingMainAttribute(false)
            setFieldValue('firstAttribute', [])
          }}
          variationType={customAttributesTypes[0]}
          className="mt-4"
        />
      )}

      {isEditMode && !isAddingMainAttribute && !!customAttributesTypes[0] && (
        <Button
          color="primary"
          startIcon={<AddOutlinedIcon color="primary" />}
          variant="outlined"
          onClick={() => {
            setIsAddingMainAttribute(true)
            setVariationType('first', customAttributesTypes[0])
            setFieldValue('firstAttribute', [])
          }}
          className="mt-4"
        >
          {`${i18n.t('productNew.add')} ${i18n.t(
            `multivariation.types.${customAttributesTypes[0]}`
          )}`}
        </Button>
      )}
    </>
  )
}

export default VariationsAccordion
